// filepath: src/views/auth/signIn/index.jsx
import React, { useState } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { MdOutlineRemoveRedEye } from 'react-icons/md';
import { RiEyeCloseLine } from 'react-icons/ri';
import DefaultAuth from 'layouts/auth/types/Default';
import illustration from 'assets/img/logo.png';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { GOLD_BORDER as goldBorder, GOLD_TEXT as goldText } from 'constants/brandColors';

const SignIn = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const textColor = useColorModeValue('navy.700', 'white');
  const textColorSecondary = 'gray.400';
  const labelColor = useColorModeValue(goldText, goldBorder);
  const toast = useToast();
  const history = useHistory();

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const loginUrl = `${process.env.REACT_APP_NEWS_API_URL}/users/userlogin`;

    try {
      const response = await axios.post(loginUrl, { email, password });

      const data = response.data;
      const expiryTime = 28800000; // 8 hours in milliseconds
      const expiryDate = new Date().getTime() + expiryTime;

      const role = data.user.role || {};
      const rawPrivileges = role.privileges || role.permissions || [];
      // Server sends privilege flags camelCase (pageAccess, readAllAccess, ...);
      // existing screens (UsersList, insertRole, hasPermission) read snake_case
      // (page_access, read_all_access, ...) from the old backend's shape, so we
      // adapt here rather than touch every consumer. Verify field names against
      // a real GET /api/role response — this mapping is best-effort per the docs.
      const permissions = rawPrivileges.map((p) => ({
        page_id: p.pageId ?? p.page_id,
        page_name: p.page?.pageName ?? p.pageName ?? p.page_name,
        route_path: p.page?.routePath ?? p.routePath ?? p.route_path,
        page_access: p.pageAccess ?? p.page_access,
        read_all_access: p.readAllAccess ?? p.read_all_access,
        read_one_access: p.readOneAccess ?? p.read_one_access,
        insert_access: p.insertAccess ?? p.insert_access,
        update_access: p.updateAccess ?? p.update_access,
        delete_access: p.deleteAccess ?? p.delete_access,
        export_access: p.exportAccess ?? p.export_access,
      }));

      sessionStorage.setItem('accessToken', data.authorization.token);
      sessionStorage.setItem('accessTokenExpiry', expiryDate.toString());
      sessionStorage.setItem('role_id', role.id ?? role.roleId);
      sessionStorage.setItem('user_id', data.user.id);
      sessionStorage.setItem('email', data.user.email);
      sessionStorage.setItem('permissions', JSON.stringify({ ...role, permissions }));
      sessionStorage.setItem('loginTimestamp', new Date().toISOString());

      setIsLoading(false);
      history.push('/admin/Main Dashboard');
    } catch (error) {
      setIsLoading(false);

      if (error.response && error.response.data && error.response.data.errors) {
        const errors = error.response.data.errors;
        const messages = Object.values(errors).flat();
        toast({
          title: 'Login error',
          description: messages.join(' '),
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      } else {
        setError('User name or password is incorrect.');
        toast({
          title: 'Login error',
          description: 'User name or password is incorrect.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      }
    }
  };

  return (
    <DefaultAuth illustrationBackground={illustration} image={illustration}>
      <Box display={{ base: "block", md: "none" }} w="100%" textAlign="center" mt={8} mb={2}>
        <img
          src={illustration}
          alt="Al Ameen News Network Logo"
          style={{ width: 120, height: 120, objectFit: "contain", borderRadius: 16, margin: "0 auto" }}
        />
      </Box>
      <Flex
        maxW={{ base: '100%', md: 'max-content' }}
        w="100%"
        mx={{ base: 'auto', lg: '0px' }}
        me="auto"
        h="100%"
        alignItems="start"
        justifyContent="center"
        mb={{ base: '30px', md: '60px' }}
        px={{ base: '25px', md: '0px' }}
        mt={{ base: '40px', md: '14vh' }}
        flexDirection="column"
      >
        <Box me="auto" textAlign="center">
          <Heading color={goldText} fontSize="36px" ml="20px" mb="4px">
            Al Ameen News Admin Panel
          </Heading>
          <Text color={goldBorder} fontWeight="600" fontSize="lg" ml="20px" mb="10px">
            شبكة الأمين الإخبارية
          </Text>
          <Text
            mb="36px"
            ms="4px"
            color={textColorSecondary}
            fontWeight="400"
            fontSize="md"
            ml="35px"
          >
            Enter your Email and password to sign in!
          </Text>
        </Box>

        <Flex
          zIndex="2"
          direction="column"
          w={{ base: '100%', md: '420px' }}
          maxW="100%"
          background="transparent"
          borderRadius="15px"
          mx={{ base: 'auto', lg: 'unset' }}
          me="auto"
          mb={{ base: '20px', md: 'auto' }}
          borderColor={goldBorder}
          p="24px"
        >
          <form onSubmit={handleLogin}>
            <FormControl>
              <FormLabel
                display="flex"
                ms="4px"
                fontSize="sm"
                fontWeight="500"
                color={textColor}
                mb="8px"
              >
                <Text color={labelColor}>Email</Text>
              </FormLabel>
              <Input
                isRequired={true}
                variant="outline"
                borderColor={goldBorder}
                focusBorderColor={goldBorder}
                fontSize="sm"
                ms={{ base: '0px', md: '0px' }}
                type="text"
                placeholder="email"
                mb="24px"
                fontWeight="500"
                size="lg"
                name="email"
                value={email}
                autoComplete="username"
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
              />
              <FormLabel
                ms="4px"
                fontSize="sm"
                fontWeight="500"
                color={textColor}
                display="flex"
              >
                <Text color={labelColor}>Password</Text>
              </FormLabel>
              <InputGroup size="md">
                <Input
                  isRequired={true}
                  fontSize="sm"
                  placeholder="password"
                  mb="24px"
                  size="lg"
                  type={showPassword ? 'text' : 'password'}
                  variant="outline"
                  borderColor={goldBorder}
                  focusBorderColor={goldBorder}
                  name="password"
                  value={password}
                  autoComplete="current-password"
                  onChange={(e) => {
                    setPassword(e.target.value);
                  }}
                />
                <InputRightElement display="flex" alignItems="center" mt="4px">
                  <Icon
                    color={textColorSecondary}
                    _hover={{ cursor: 'pointer' }}
                    as={showPassword ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                    onClick={togglePasswordVisibility}
                  />
                </InputRightElement>
              </InputGroup>
              <Button
                fontSize="sm"
                variant="outline"
                borderColor={goldBorder}
                color={goldText}
                fontWeight="500"
                w="100%"
                h="50"
                mb="24px"
                type="submit"
                _hover={{ bg: goldBorder, color: 'black' }}
                isLoading={isLoading}
              >
                Sign In
              </Button>
            </FormControl>
            {error && (
              <Text color="red.500" mb="8px">
                {error}
              </Text>
            )}
          </form>
        </Flex>
      </Flex>
    </DefaultAuth>
  );
};

export default SignIn;